import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID, Renderer2 } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import { humanize, isMobileView } from '@core/utils';
import { TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/ui';
import { TippyModule } from '@ngneat/helipopper';
import { PERMISSIONS, ROLE } from '@auth';
// The below path is relative because when injected in StandAlone component it causes circular dependency somehow, and this is the only way to fix it
import { PermissionService } from '../../../../auth/services/permissions.service';

@UntilDestroy()
@Component({
  selector: 'app-tabs-selection',
  templateUrl: './tabs-selection.component.html',
  styleUrls: ['./tabs-selection.component.scss'],
  imports: [CommonModule, RouterLink, IconComponent, TippyModule],
})

/**
 * This component is used to display a list of tabs and emit the selected tab.
 * Now supports filtering tabs based on user roles and permissions.
 *
 * @property tabs: {
 *   label: string;
 *   displayName?: string;
 *   routerLink?: string;
 *   info?: string;
 *   roles?: ROLE[];
 *   permissions?: PERMISSIONS[];
 * }[] - The list of tabs to display
 * @property activeTabBgColor: string - The background color of the active tab
 * @property activeTabTextColor: string - The text color of the active tab
 * @event selectedTab: EventEmitter<string> - The selected tab
 *
 * @example
 * <app-tabs-selection
 *   [tabs]="tabs"
 *   (selectedTab)="selectedTab($event)"
 *   activeTabBgColor="#FFFFFF"
 *   activeTabTextColor="#000000"
 *   roles="[ROLE.ADMIN, ROLE.EDITOR]"
 *   permissions="[PERMISSIONS.SOME_PERMISSION_KEY']"
 *   >
 * </app-tabs-selection>
 */
export class TabsSelectionComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * The list of tabs to display with optional role and permission filtering
   * @example
   * [{
   *   label: 'tab1',
   *   routerLink: '/tab1',
   *   displayName: 'Tab 1',
   *   roles="[ROLE.ADMIN, ROLE.EDITOR]"
   *   permissions="[PERMISSIONS.SOME_PERMISSION_KEY']"
   * }]
   */
  @Input() tabs: {
    label: string;
    displayName?: string;
    routerLink?: string;
    info?: string;
    roles?: ROLE[];
    permissions?: PERMISSIONS[];
  }[] = [];
  /**
   * Filtered tabs based on user roles and permissions
   */
  filteredTabs: typeof this.tabs = [];
  /**
   * Emits the selected tab
   */
  @Output() selectedTab: EventEmitter<string> = new EventEmitter<string>();
  /**
   * Sets the background color of the active tab
   */
  @Input() activeTabBgColor = 'var(--white)';
  @Input() activeTabBorderRadius = '6px';
  @Input() tabsSelectorBgColor = 'var(--gray-150)';
  /**
   * Sets the text color of the active tab
   */
  @Input() activeTabTextColor = isMobileView() ? ' var(--text-color-light)' : 'var(--orange)';
  /**
   * Disables the tabs
   */
  @Input() disabled = false;
  @Input() marginBottom = '0px';
  @Input() marginTop = '0px';
  activeTab = 0;
  private readonly _el = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _translate = inject(TranslateService);
  private readonly _permissionService = inject(PermissionService);
  private _resizeListener: () => void;
  private _selectedTabName: string;

  /**
   * The name of the selected tab
   * @example 'tab 1'
   */
  @Input() set selectedTabName(tabName: string) {
    this._selectedTabName = tabName;
    this._setActiveTab();
  }

  ngOnInit() {
    // Filter tabs based on user roles and permissions
    this._filterTabs();
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this._platformId)) {
      this._resizeListener = this._renderer.listen(window, 'resize', () => {
        this._setActiveTab();
      });
    }
    this._setActiveTab();

    if (this._checkIfCurrentTabsHaveRouterLink()) {
      this._setTabFromRoute();
      this._getTabRoute();
    }
    this._cdRef.detectChanges();
  }

  selectTab(index: number) {
    const buttons = this._el.nativeElement.querySelectorAll('.tab');
    const bg = this._el.nativeElement.querySelector('.tab-slider-bg');
    const activeButton = buttons[index];

    if (!activeButton) {
      return;
    }
    // Reset all button text colors to default
    buttons.forEach((button: any) => (button.style.color = ''));

    // Set the width and position of the slider background
    bg.style.width = `${activeButton.offsetWidth}px`;
    bg.style.left = `${activeButton.offsetLeft}px`;
    bg.style.backgroundColor = this.activeTabBgColor; // set the background color from input

    // Set the text color for the active tab
    activeButton.style.color = this.activeTabTextColor;

    // Update active tab index
    this.activeTab = index;

    // Emit the selected tab
    setTimeout(() => {
      const selectedTab = this.filteredTabs[index];
      if (selectedTab.displayName) {
        this.selectedTab.emit(selectedTab.label);
      } else {
        const enTranslation = this._translate.instant(selectedTab.label);
        this.selectedTab.emit(enTranslation);
      }
    });
  }

  ngOnDestroy() {
    if (this._resizeListener) {
      this._resizeListener();
    }
  }

  /**
   * Filter tabs based on user roles and permissions
   * Specificity order: Permissions > Roles
   */
  private _filterTabs(): void {
    this.filteredTabs = this.tabs.filter((tab) => {
      // If permissions are provided, check permissions first
      if (tab.permissions && tab.permissions.length > 0) {
        // Normalize permissions to always be an array
        const permissionsToCheck = Array.isArray(tab.permissions) ? tab.permissions : [tab.permissions];

        return permissionsToCheck.some((permission) => this._permissionService.hasPermission(permission));
      }

      // If roles are provided, check roles
      if (tab.roles && tab.roles.length > 0) {
        return this._permissionService.hasRole(tab.roles);
      }

      // If no roles or permissions are specified, show the tab
      return true;
    });

    // If no tabs are left after filtering, default to first tab
    if (this.filteredTabs.length === 0) {
      this.filteredTabs = this.tabs.slice(0, 1);
    }
  }

  private _setActiveTab(): void {
    if (this._selectedTabName) {
      let tabIndex;
      if (this._checkIfCurrentTabsHaveRouterLink()) {
        tabIndex = this.filteredTabs.findIndex((tab) => {
          return humanize(tab.label).toLowerCase() === this._selectedTabName.toLowerCase() || tab.routerLink
            ? tab.routerLink === this._selectedTabName || tab.routerLink.includes(this._selectedTabName)
            : false;
        });
      } else {
        tabIndex = this.filteredTabs.findIndex((tab) => {
          return humanize(tab.label).toLowerCase() === this._selectedTabName.toLowerCase();
        });
      }

      if (tabIndex !== -1) {
        this.selectTab(tabIndex);
      } else {
        this.selectTab(0);
        setTimeout(() => {
          this.selectedTab.emit(this._selectedTabName);
        });
      }
    } else {
      this.selectTab(0);
    }
  }

  private _setTabFromRoute(): void {
    let activeRoute = this._activatedRoute;
    let lastSegment = '';

    // Traverse to the last child of the activated route
    while (activeRoute.firstChild && activeRoute.firstChild.snapshot && activeRoute.firstChild.snapshot.url.length > 0) {
      activeRoute = activeRoute.firstChild;
    }

    // Check if the last child route has a path segment
    if (activeRoute.snapshot.url.length > 0) {
      lastSegment = activeRoute.snapshot.url[activeRoute.snapshot.url.length - 1].path;
    } else {
      // If no child route, use the second segment of the full URL
      const fullPathSegments = this._router.url.split('/');
      lastSegment = fullPathSegments.length > 1 ? fullPathSegments[1] : '';
    }

    this.selectedTabName = lastSegment ? (lastSegment === 'm' ? this.filteredTabs[0].label.toLowerCase() : lastSegment) : this.filteredTabs[0].label.toLowerCase();
  }

  private _getTabRoute(): void {
    this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this._setTabFromRoute();
        },
      });
  }

  private _checkIfCurrentTabsHaveRouterLink(): boolean {
    return this.filteredTabs.some((tab) => tab.routerLink);
  }
}
